/**
 * F-FAKE-DIR-PICKER — in-memory `FileSystemDirectoryHandle` shim for the
 * J vault-export-folder spec. Headless Chromium typically lacks
 * `window.showDirectoryPicker`; tests call
 * `__repancyTestInstallFakeDirectoryPicker()` once and then
 * `exportToFolder()` runs against the fake handle exactly like a real
 * pick.
 *
 * Captures each write path so the spec can assert `getFakeWrites()`
 * length + extension after the export.
 *
 * Self-gated on `?test=1` by the caller (installTestHooks).
 */

interface FakeWritableStream {
  write: (data: Blob | ArrayBuffer | string) => Promise<void>;
  close: () => Promise<void>;
}

interface FakeFileHandle {
  createWritable: () => Promise<FakeWritableStream>;
}

interface FakeDirectoryHandle {
  name: string;
  getDirectoryHandle: (
    name: string,
    opts?: { create?: boolean },
  ) => Promise<FakeDirectoryHandle>;
  getFileHandle: (
    name: string,
    opts?: { create?: boolean },
  ) => Promise<FakeFileHandle>;
}

const writes: string[] = [];

function createFakeDirectoryHandle(name: string, basePath: string): FakeDirectoryHandle {
  return {
    name,
    async getDirectoryHandle(childName) {
      const childPath = basePath === '' ? childName : `${basePath}/${childName}`;
      return createFakeDirectoryHandle(childName, childPath);
    },
    async getFileHandle(fileName) {
      const filePath = basePath === '' ? fileName : `${basePath}/${fileName}`;
      return {
        async createWritable() {
          return {
            async write() {
              // We only need to record the path; size is irrelevant
              // for spec assertions.
            },
            async close() {
              if (!writes.includes(filePath)) writes.push(filePath);
            },
          };
        },
      };
    },
  };
}

export function getFakeWrites(): readonly string[] {
  return [...writes];
}

export function resetFakeWrites(): void {
  writes.length = 0;
}

/**
 * Replace `window.showDirectoryPicker` with a fake that resolves a
 * FakeDirectoryHandle. Each call returns a fresh root so concurrent
 * test scenarios don't share state, but `writes` is module-scoped
 * (cleared via `resetFakeWrites` or `resetTestHooks`).
 */
export function installFakeDirectoryPicker(): void {
  (window as unknown as {
    showDirectoryPicker?: () => Promise<FakeDirectoryHandle>;
  }).showDirectoryPicker = async () =>
    createFakeDirectoryHandle('repancy-test-export', '');
}

export function uninstallFakeDirectoryPicker(): void {
  delete (window as unknown as { showDirectoryPicker?: unknown }).showDirectoryPicker;
}
